import {Office, Role, User} from "../../api/Model";
import Permission, {Offices_Read} from "./Permission";
import {rolePermissions} from "./AccessRules";
import {AccessibleOffices} from "./AccessibleOffices";
import {AccessibleVaults} from "./AccessibleVaults";
import {applyFilter, filterValue} from "../FilterUtil";
import {Opt} from "../Optional";
import {maxBy, numberCmp} from "../SortUtil";

export {rolesOf} from "./AccessRules";
export {AccessibleOffices} from "./AccessibleOffices";

export type PermissionCheck = Permission | readonly Permission[];


export class Access {
    private readonly permissions: ReadonlySet<Permission>;
    readonly canAccessAnyOffice: boolean;
    readonly accessibleOffices: AccessibleOffices;
    readonly accessibleVaults: AccessibleVaults;
    readonly isJetBrains: boolean;
    readonly officeRoles: readonly Role[];

    constructor(
        user: User | null,
        readonly currentOfficeId: Office['id'] | null,
    ) {
        const roles = user?.roles ?? [];
        this.officeRoles = applyFilter(
            roles,
            filterValue(
                Access.matchRoleOffice(currentOfficeId),
                role => role
            )
        );
        this.permissions = new Set<Permission>(
            this.officeRoles.flatMap(role => [...rolePermissions[role.type]])
        );
        this.canAccessAnyOffice = roles.some(role => rolePermissions[role.type].has(Offices_Read));
        this.accessibleOffices = new AccessibleOffices(roles);
        this.accessibleVaults = new AccessibleVaults(this.officeRoles);
        this.isJetBrains = (user?.email === 'maxim.mazin@gmail.com' ?? false) || (user?.email?.toLocaleLowerCase()?.endsWith('@jetbrains.com') ?? false);
    }

    has(requiredPermission: PermissionCheck): boolean {
        if (typeof requiredPermission === 'string') {
            return this.permissions.has(requiredPermission);
        } else {
            return requiredPermission.every(permission => this.permissions.has(permission));
        }
    }

    maxRole(): Role {
        return maxBy(this.officeRoles, numberCmp(role => {
            switch (role.type) {
                case 'guest':
                    return 0;
                case 'vault-manager':
                    return 1;
                case 'office-admin':
                    return 2;
                case 'system-admin':
                    return 3;
            }
        })) ?? {type: 'guest'};
    }

    matchesJetBrains(requiredJetBrains: boolean | undefined): boolean {
        return !requiredJetBrains || this.isJetBrains;
    }

    static matchesOffice(role: Role, officeId: Office['id'] | null) {
        if (officeId === null) return true;
        switch (role.type) {
            case 'guest':
                return false;
            case 'vault-manager':
                return role.officeId === officeId;
            case 'office-admin':
                return role.officeId === officeId;
            case 'system-admin':
                return true;
        }
    }

    static matchRoleOffice(officeId: Opt<Office['id']>): Opt<(role: Role) => boolean> {
        if (officeId === null || officeId === undefined) {
            return undefined;
        } else {
            return (role: Role) => {
                switch (role.type) {
                    case 'guest':
                        return false;
                    case 'vault-manager':
                        return role.officeId === officeId;
                    case 'office-admin':
                        return role.officeId === officeId;
                    case 'system-admin':
                        return true;
                }
            }
        }
    }

}
